import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Calendar, User, Eye, Heart, MessageCircle, Share2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);

  useEffect(() => {
    fetchBlogDetail();
  }, [id]);

  const fetchBlogDetail = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blogs/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setBlog(data.blog);
        // Mock comments for now
        setComments([
          {
            id: 1,
            author: 'Dr. Sarah Johnson',
            content: 'Great insights! This will definitely help improve our referral process.',
            timestamp: '2 hours ago'
          },
          {
            id: 2,
            author: 'Dr. Michael Chen',
            content: 'Thanks for sharing this detailed guide.',
            timestamp: '5 hours ago'
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blogs/${id}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setLiked(!liked);
      setBlog(prev => ({
        ...prev,
        likes: liked ? prev.likes - 1 : prev.likes + 1
      }));
    } catch (error) {
      console.error('Error liking blog:', error);
    }
  };

  const handleComment = () => {
    if (!comment.trim()) return;
    
    const newComment = {
      id: comments.length + 1,
      author: user?.name || 'Anonymous',
      content: comment,
      timestamp: 'Just now'
    };
    
    setComments([newComment, ...comments]);
    setComment('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Article Not Found</h2>
          <Button onClick={() => navigate('/blog')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <Button
        variant="ghost"
        onClick={() => navigate('/blog')}
        className="mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Blog
      </Button>

      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center space-x-2 mb-4">
            <Badge className={blog.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
              {blog.published ? 'Published' : 'Draft'}
            </Badge>
            <Badge variant="outline">{blog.category}</Badge>
          </div>
          <CardTitle className="text-3xl mb-4">{blog.title}</CardTitle>
          
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <User className="h-4 w-4 mr-1" />
                {blog.author_name}
              </div>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {new Date(blog.created_at).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="flex items-center">
                <Eye className="h-4 w-4 mr-1" />
                {blog.views || 0}
              </span>
              <span className="flex items-center">
                <Heart className="h-4 w-4 mr-1" />
                {blog.likes || 0}
              </span>
              <span className="flex items-center">
                <MessageCircle className="h-4 w-4 mr-1" />
                {comments.length}
              </span>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="prose max-w-none mb-8">
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">{blog.excerpt}</p>
            <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">
              {blog.content}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 pt-6 border-t">
            <Button
              variant={liked ? "default" : "outline"}
              onClick={handleLike}
              className={liked ? "bg-red-500 hover:bg-red-600" : ""}
            >
              <Heart className={`h-4 w-4 mr-2 ${liked ? 'fill-white' : ''}`} />
              {liked ? 'Liked' : 'Like'}
            </Button>
            <Button variant="outline">
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Comments Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <MessageCircle className="h-5 w-5 mr-2 text-[#D4AF37]" />
            Comments ({comments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Add Comment */}
          <div>
            <Textarea
              placeholder="Add your comment..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className="mb-3"
            />
            <Button 
              onClick={handleComment}
              className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
              disabled={!comment.trim()}
            >
              Post Comment
            </Button>
          </div>

          {/* Comments List */}
          <div className="space-y-4">
            {comments.map((cmt) => (
              <div key={cmt.id} className="flex space-x-3 p-4 border rounded-lg">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-[#0A1628] text-[#D4AF37]">
                    {cmt.author.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-[#0A1628]">{cmt.author}</p>
                    <p className="text-xs text-gray-500">{cmt.timestamp}</p>
                  </div>
                  <p className="text-gray-700">{cmt.content}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BlogDetail;
